import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useBottomMenu } from "../../../hooks/useBottomMenu";
import { useDefinitionFeatures, useSpreadFeatures } from "../../../hooks/useDefinitionFeatures";
import { useGoogleAnalytics } from "../../../hooks/useGoogleAnalytics";
import { useHasBottomRootMenu } from "../../../hooks/useHasBottomRootMenu";
import { useLabels } from "../../../hooks/useLabels";
import { useLogicalSelectedPage, useSelectedZone } from "../../../hooks/useSelection";
import { useTemplate } from "../../../hooks/useTemplate";
import { useSetColorBackground } from "../../bottom-background-menus/background-color-panel/hooks/useSetColorBackground";
import { useSetPatternBackground } from "../../bottom-background-menus/background-pattern-panel/hooks/useSetPatternBackground";
import { BottomRootMenu, IBottomRootMenuRef } from "./bottom-root-menu";

import { useAlternativeProducts } from "../../../hooks/useAlternativeProducts";
import { useIsProductAdvancedEditMode } from "../../../hooks/useIsProductAdvancedEditMode";
import { useProductOptions } from "../../../hooks/useProductOptions";
import { useSpreadDesign } from "../../../hooks/useSpreadDesign";
import { useSupportedProductStartDate } from "../../../hooks/useSupportedProductStartDate";
import { useSelectedAlternativeProductGroupState } from "../../bottom-menu-panels/alternative-products/hooks";
import { IBottomMenuButton } from "../../bottom-menu/buttons";
import { BottomMenuIds, BottomMenuPaths, ROOT_SUB_MENU_ID } from "../../bottom-menu/paths";
import { labels } from "./labels";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useSymbolsCount } from "../../../hooks/useSymbols";
import { isDeleteSymbolZoneSupported } from "../../bottom-menu-panels/symbols/menu-info";
import { BottomMenuButton } from "../../bottom-menu/bottom-menu-button";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useMapZones } from "./use-map-zones";
import { isBottomPhotoMenuSupported } from "../../bottom-photo-menus/bottom-photo-menu/menu-info";
import { useQrSupport } from "../../../hooks/use-qr-support";
import { QrIcon } from "../../../icons/icons";

/** Used to know if it can be added in the bottom menu */
export function isBottomRootMenuSupported() {
	return true;
}

export const BottomRootMenuContainer = () => {
	const [
		[
			optionsTitle,
			photoMenuTitle,
			mapMenuTitle,
			textMenuTitle,
			backgroundsMenuTitle,
			templatesMenuTitle,
			designsMenuTitle,
			symbolsMenuTitle,
		],
	] = useLabels([
		labels.options,
		labels.photo,
		labels.map,
		labels.text,
		labels.backgrounds,
		labels.templates,
		labels.designs,
		labels.symbols,
	]);

	const bottomRootMenuRef = useRef<IBottomRootMenuRef>(null);

	const { alternativeProductsGroups, isInitialLoading: areAlternativeProductsLoading } =
		useAlternativeProducts();

	const { productOptionGroup, isInitialLoading: areProductOptionsLoading } = useProductOptions();
	const isProductStartDateSupported = useSupportedProductStartDate();
	const hasProductAlternatives = !!alternativeProductsGroups?.length;
	const hasProductOptions = !!productOptionGroup;
	const showOptionsMenu =
		hasProductAlternatives || hasProductOptions || isProductStartDateSupported;

	const { gotoPath, currentPath } = useBottomMenu();
	const selectedZone = useSelectedZone();
	const canChangeColorBackgrounds = !!useSetColorBackground();
	const canChangePatternBackgrounds = !!useSetPatternBackground();
	const canChangeBackground = canChangeColorBackgrounds || canChangePatternBackgrounds;

	const { openZoneMapAsync, isMapsSupportedOnDefinition: showMapsButton } = useMapZones();
	const canChangeMap = !!openZoneMapAsync;

	const isAdvancedEditModeEnabled = useIsProductAdvancedEditMode();

	const selectedPage = useLogicalSelectedPage();
	const [, , , canApplyTemplate] = useTemplate();
	const canChangeTemplate = canApplyTemplate(selectedPage);

	const [, applySpreadDesign] = useSpreadDesign();
	const dashboard = useDashboardContext();
	const canChangeSpreadDesign = !!applySpreadDesign;

	// ATM no QR Edit button, only allow if QR Add or Delete is supported
	const {
		isAddSupported: isQrAddSupported,
		isDeleteSupported: isQrDeleteSupported,
		isEditSupported: isQrEditSupported,
		isAddEnabled: isQrAddEnabled,
		isEditEnabled: isQrEditEnabled,
		isDeleteEnabled: isQrDeleteEnabled,
	} = useQrSupport(selectedPage, selectedZone);

	const isQrSupported = isQrAddSupported || isQrDeleteSupported || isQrEditSupported;
	const isQrEnabled = isQrAddEnabled || isQrDeleteEnabled || isQrEditEnabled;

	const googleAnalytics = useGoogleAnalytics();
	const { createErrorHandler } = useErrorHandling();

	const symbolsCount = useSymbolsCount();
	const {
		areBackgroundsSupported,
		areBackgroundColorsSupported,
		areBackgroundPatternsSupported,
		areTemplatesSupported,
		hasFillablePhotoZonesInDefinition,
		hasFillableTextZonesInDefinition,
		areDesignsSupported,
		arePageDesignsSupported,
		areSymbolsSupported,
	} = useDefinitionFeatures();

	const { hasFillableSymbolZonesInSpread } = useSpreadFeatures();

	const hasBottomRootMenu = useHasBottomRootMenu();

	const [, setAlternativeProductGroup] = useSelectedAlternativeProductGroupState();

	const onClick = useCallback(
		(menuId: ROOT_SUB_MENU_ID) => {
			const logGaEvent = (itemId: string) => {
				switch (itemId) {
					case BottomMenuIds.Photo: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootPhotosClicked",
						});
						break;
					}
					case BottomMenuIds.Map: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootMapsClicked",
						});
						break;
					}
					case BottomMenuIds.Qr: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootQrClicked",
						});
						break;
					}
					case BottomMenuIds.Text: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootTextClicked",
						});
						break;
					}
					case BottomMenuIds.Backgrounds: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootBackgroundsClicked",
						});
						break;
					}
					case BottomMenuIds.Layouts: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootLayoutsClicked",
						});
						break;
					}
					case BottomMenuIds.Designs: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootDesignsClicked",
						});
						break;
					}
					case BottomMenuIds.Symbols: {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootSymbolsClicked",
						});
						break;
					}
					default:
						break;
				}
			};

			// Log Google Analytics event
			logGaEvent(menuId);

			// Open the right sub menu
			switch (menuId) {
				case BottomMenuIds.Options: {
					if (
						hasProductAlternatives &&
						alternativeProductsGroups.length === 1 &&
						!hasProductOptions &&
						!isProductStartDateSupported
					) {
						setAlternativeProductGroup(alternativeProductsGroups[0]);
						gotoPath(BottomMenuPaths.AlternativeProducts);
					} else if (
						hasProductOptions &&
						!hasProductAlternatives &&
						!isProductStartDateSupported
					) {
						gotoPath(BottomMenuPaths.ProductOptions);
					} else {
						gotoPath(BottomMenuPaths.Options);
					}
					break;
				}
				case BottomMenuIds.Photo: {
					gotoPath(
						// Show only photo-selection panel when an empty photozone is selected
						!eXpress.core.isPhotoZone(selectedZone) ||
							(selectedZone.isEmpty && selectedZone.isZoneForPhotoSourceType("photo"))
							? BottomMenuPaths.PhotoSelection
							: BottomMenuPaths.Photo,
					);
					break;
				}
				case BottomMenuIds.Map: {
					openZoneMapAsync?.().catch(createErrorHandler());
					break;
				}
				case BottomMenuIds.Qr: {
					gotoPath(BottomMenuPaths.Qr);
					break;
				}
				case BottomMenuIds.Text: {
					gotoPath(BottomMenuPaths.Text);
					break;
				}
				case BottomMenuIds.Backgrounds: {
					if (areBackgroundColorsSupported && !areBackgroundPatternsSupported) {
						gotoPath(BottomMenuPaths.BackgroundColor);
					} else if (!areBackgroundColorsSupported && areBackgroundPatternsSupported) {
						gotoPath(BottomMenuPaths.BackgroundPattern);
					} else {
						gotoPath(BottomMenuPaths.Backgrounds);
					}
					break;
				}
				case BottomMenuIds.Layouts: {
					gotoPath(
						currentPath === BottomMenuPaths.Layouts
							? BottomMenuPaths.Root
							: BottomMenuPaths.Layouts,
					);
					break;
				}
				case BottomMenuIds.Designs: {
					gotoPath(
						currentPath === BottomMenuPaths.Designs
							? BottomMenuPaths.Root
							: BottomMenuPaths.Designs,
					);
					break;
				}
				case BottomMenuIds.Symbols: {
					if (isAdvancedEditModeEnabled) {
						gotoPath(
							currentPath === BottomMenuPaths.Symbols
								? BottomMenuPaths.Root
								: BottomMenuPaths.Symbols,
						);
					} else if (isDeleteSymbolZoneSupported(selectedZone, dashboard)) {
						gotoPath(
							currentPath === BottomMenuPaths.Symbols
								? BottomMenuPaths.Root
								: BottomMenuPaths.Symbols,
						);
					} else {
						gotoPath(
							currentPath === BottomMenuPaths.SymbolsList
								? BottomMenuPaths.Root
								: BottomMenuPaths.SymbolsList,
						);
					}
					break;
				}
				default: {
					throw new Error("No bottom menu navigation");
				}
			}
		},
		[
			alternativeProductsGroups,
			areBackgroundColorsSupported,
			areBackgroundPatternsSupported,
			currentPath,
			dashboard,
			openZoneMapAsync,
			googleAnalytics,
			gotoPath,
			hasProductAlternatives,
			hasProductOptions,
			isAdvancedEditModeEnabled,
			isProductStartDateSupported,
			selectedZone,
			setAlternativeProductGroup,
			createErrorHandler,
		],
	);

	const showTextButton = hasFillableTextZonesInDefinition || isAdvancedEditModeEnabled;
	const showPhotoButton = hasFillablePhotoZonesInDefinition || isAdvancedEditModeEnabled;
	const canUsePhotoButton = isBottomPhotoMenuSupported(undefined, dashboard, selectedPage);

	const menuItems = useMemo(() => {
		const items: IBottomMenuButton<ROOT_SUB_MENU_ID>[] = [];

		if (showPhotoButton) {
			items.push({
				id: BottomMenuIds.Photo,
				iconName: "photo",
				title: photoMenuTitle,
				isDisabled: !canUsePhotoButton,
				onClick: () => onClick(BottomMenuIds.Photo),
			});
		}

		if (showMapsButton) {
			items.push({
				id: BottomMenuIds.Map,
				iconName: "map",
				title: mapMenuTitle,
				isDisabled: !canChangeMap,
				badgeText: "BETA",
				onClick: () => onClick(BottomMenuIds.Map),
			});
		}

		if (isQrSupported) {
			items.push({
				id: BottomMenuIds.Qr,
				children: <QrIcon />,
				title: "QR",
				isDisabled: !isQrEnabled,
				badgeText: "BETA",
				onClick: () => onClick(BottomMenuIds.Qr),
			});
		}

		if (showTextButton) {
			items.push({
				id: BottomMenuIds.Text,
				iconName: "text",
				title: textMenuTitle,
				isDisabled: !showTextButton,
				onClick: () => onClick(BottomMenuIds.Text),
			});
		}

		if (areBackgroundsSupported) {
			items.push({
				id: BottomMenuIds.Backgrounds,
				iconName: "paint-bucket",
				title: backgroundsMenuTitle,
				isDisabled: !canChangeBackground,
				isActive: currentPath === BottomMenuPaths.Backgrounds,
				onClick: () => onClick(BottomMenuIds.Backgrounds),
			});
		}

		if (areTemplatesSupported || arePageDesignsSupported) {
			items.push({
				id: BottomMenuIds.Layouts,
				iconName: "templates",
				title: templatesMenuTitle,
				isDisabled: !(canChangeTemplate || canChangeSpreadDesign),
				isActive: currentPath === BottomMenuPaths.Layouts,
				onClick: () => onClick(BottomMenuIds.Layouts),
			});
		}

		if (areSymbolsSupported && !!symbolsCount) {
			items.push({
				id: BottomMenuIds.Symbols,
				iconName: "symbols",
				title: symbolsMenuTitle,
				isDisabled:
					// disabled if there are no fillable zones OR if advanced edit mode can't add symbolzones
					!(
						hasFillableSymbolZonesInSpread ||
						(selectedPage && dashboard.canAddSymbolZone(selectedPage))
					),
				isActive: currentPath === BottomMenuPaths.Symbols,
				onClick: () => onClick(BottomMenuIds.Symbols),
			});
		}

		if (areDesignsSupported) {
			items.push({
				id: BottomMenuIds.Designs,
				iconName: "designs",
				title: designsMenuTitle,
				isDisabled: false,
				isActive: currentPath === BottomMenuPaths.Designs,
				onClick: () => onClick(BottomMenuIds.Designs),
			});
		}

		if (showOptionsMenu) {
			items.push({
				id: BottomMenuIds.Options,
				iconName: "options",
				title: optionsTitle,
				isDisabled: !showOptionsMenu,
				onClick: () => onClick(BottomMenuIds.Options),
			});
		}

		return items;
	}, [
		showPhotoButton,
		showMapsButton,
		isQrSupported,
		showTextButton,
		areBackgroundsSupported,
		areTemplatesSupported,
		arePageDesignsSupported,
		areSymbolsSupported,
		symbolsCount,
		areDesignsSupported,
		showOptionsMenu,
		photoMenuTitle,
		canUsePhotoButton,
		onClick,
		mapMenuTitle,
		canChangeMap,
		isQrEnabled,
		textMenuTitle,
		backgroundsMenuTitle,
		canChangeBackground,
		currentPath,
		templatesMenuTitle,
		canChangeTemplate,
		canChangeSpreadDesign,
		symbolsMenuTitle,
		hasFillableSymbolZonesInSpread,
		selectedPage,
		dashboard,
		designsMenuTitle,
		optionsTitle,
	]);

	useEffect(() => {
		if (menuItems) {
			const activeIndex = menuItems.findIndex((item) => item.isActive);
			bottomRootMenuRef.current?.scrollIntoView(activeIndex);
		}
	}, [menuItems]);

	if (!hasBottomRootMenu) return null;
	if (areAlternativeProductsLoading || areProductOptionsLoading) return null;

	return (
		<BottomRootMenu
			ref={bottomRootMenuRef}
			showBackButton={false}
			fadeRight
			className="bottom-root-menu"
		>
			{menuItems.map((item) => (
				<BottomMenuButton key={item.id} {...item} iconName={item.iconName} />
			))}
		</BottomRootMenu>
	);
};
BottomRootMenuContainer.displayName = "BottomRootMenuContainer";
